import React from 'react';
import { Link } from 'react-router-dom';
import { getFullImageUrl, handleImageError } from '../../utils/imageUtils';
import BlurFade from './motion/BlurFade';

export interface NetworkItem {
  _id: string;
  title: string;
  apy?: string;
  imageUrl?: string;
  stakingLink?: string;
}

const PRIORITY = ['celestia', 'espresso', 'starknet', 'monad'];

export function sortNetworks<T extends { title: string; apy?: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const ai = PRIORITY.findIndex((n) => a.title.toLowerCase().includes(n));
    const bi = PRIORITY.findIndex((n) => b.title.toLowerCase().includes(n));
    const ap = ai === -1 ? 100 + a.title.localeCompare(b.title) : ai;
    const bp = bi === -1 ? 100 + b.title.localeCompare(a.title) : bi;
    if (ap !== bp) return ap - bp;
    const aOk = !!(a.apy && a.apy.trim());
    const bOk = !!(b.apy && b.apy.trim());
    if (aOk && !bOk) return -1;
    if (!aOk && bOk) return 1;
    return a.title.localeCompare(b.title);
  });
}

function formatApy(apy?: string): { text: string; tone: 'ok' | 'low' | 'soon' } {
  if (!apy || !apy.trim()) return { text: 'Soon', tone: 'soon' };
  const n = parseFloat(apy);
  if (Number.isNaN(n)) return { text: 'Soon', tone: 'soon' };
  if (n <= 0) return { text: `~${n}%`, tone: 'low' };
  return { text: `~${n}%`, tone: 'ok' };
}

const toneClass = {
  ok: 'text-[#3ecf8e]',
  low: 'text-[#e85d5d]',
  soon: 'text-dusk-muted',
} as const;

interface NetworkGridProps {
  items: NetworkItem[];
  delayBase?: number;
}

const NetworkGrid: React.FC<NetworkGridProps> = ({ items, delayBase = 0.03 }) => {
  const sorted = sortNetworks(items);

  return (
    <div
      className="grid gap-2.5 md:gap-3"
      style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}
    >
      {sorted.map((p, i) => {
        const { text, tone } = formatApy(p.apy);
        const inner = (
          <>
            {p.imageUrl ? (
              <img
                src={getFullImageUrl(p.imageUrl)}
                alt=""
                className="h-9 w-9 object-contain rounded-full"
                onError={handleImageError}
              />
            ) : (
              <div className="h-9 w-9 rounded-full bg-[rgba(243,235,226,0.05)] border border-[var(--dusk-line)] text-dusk-ink flex items-center justify-center font-display text-sm">
                {p.title.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="text-[0.62rem] font-semibold tracking-[0.12em] uppercase text-dusk-ink/90 text-center leading-tight">
              {p.title}
            </span>
            <span className={`text-xs font-semibold tabular-nums ${toneClass[tone]}`}>{text}</span>
          </>
        );

        const cardCls =
          'flex flex-col items-center justify-center gap-1.5 w-full h-full min-h-[6.5rem] px-2 py-3 rounded-xl bg-[#0e0d0c] border border-[rgba(243,235,226,0.05)] no-underline transition-colors hover:border-[rgba(208,0,104,0.35)] hover:bg-[#121010]';

        return (
          <BlurFade key={p._id} delay={delayBase * i} className="min-w-0">
            {p.stakingLink ? (
              <a href={p.stakingLink} target="_blank" rel="noopener noreferrer" className={cardCls}>
                {inner}
              </a>
            ) : (
              <Link to="/stake" className={`${cardCls} opacity-80`}>
                {inner}
              </Link>
            )}
          </BlurFade>
        );
      })}
    </div>
  );
};

export default NetworkGrid;
